<template>
  <table
    v-if="appointmentList.length > 0"
    class="my-3 table-auto"
    aria-label="AppointmentList"
  >
    <thead class="uppercase">
    <tr>
      <th>Ne-Nr</th>
      <th>Stockwerk</th>
      <th>Klingelschild</th>
      <th>Uhr</th>
    </tr>
    </thead>
    <tbody>
    <template v-for="(row) in rows" :key="row.key">
      <tr v-if="row.showDay">
        <td :colspan="4" class="py-2">
          <div class="flex items-center">
            <span class="text-xs font-medium opacity-70 mr-2">{{ formatDay(row.obj.appointment.start) }}</span>
          </div>
        </td>
      </tr>
      <tr>
        <td>{{ row.obj.neNr }}</td>
        <td>{{ row.obj.floor }}</td>
        <td>{{ row.obj.doorbellName }}</td>
        <td>{{ formatTimeRange(row.obj.appointment.start, row.obj.appointment.end) }}</td>
      </tr>
    </template>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { formatTime } from '@/utils/dateUtils'
import type { OrderInstallerAppointmentRoomGroup } from '@/models/types/Order'

const props = defineProps<{
  appointmentList: Array<OrderInstallerAppointmentRoomGroup>
}>()

const sortedAppointments = computed(() =>
  [...props.appointmentList].sort(
    (a, b) => new Date(a.appointment.start).getTime() - new Date(b.appointment.start).getTime()
  )
)

const dayEpochs = computed(() =>
  sortedAppointments.value.map(a => {
    const d = new Date(a.appointment.start)
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  })
)

const hasMultipleDays = computed(() => new Set(dayEpochs.value).size > 1)

const rows = computed(() =>
  sortedAppointments.value.map((obj, idx) => {
    const showDay =
      hasMultipleDays.value &&
      (idx === 0 || dayEpochs.value[idx] !== dayEpochs.value[idx - 1])
    return {
      obj,
      showDay,
      key: `${new Date(obj.appointment.start).toISOString()}-${idx}`
    }
  })
)

function formatTimeRange(start: string | Date, end: string | Date) {
  return `${formatTime(start)} - ${formatTime(end)}`
}

function formatDay(date: string | Date, locale = 'de-DE') {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(d)
}
</script>